import { useState, useRef, useEffect, useCallback } from "react";
import { Input, Dialog, DialogContent, View, Image } from "@/components/ui";
import { Clock, MagnifyingGlass, Sparkle } from "@/components/shared/icons";
import { SearchInput } from "@/components/shared/search-input";
import { popularServiceItems } from "@/containers/homepage/lib";
import { ListHeading } from "../list-header";
import { IconButton } from "@/components/ui/icon-button";
import { Typography } from "../typography";
import { useRoute } from "@/components/shared/router";
import { useSearchStore } from "@/store/search-bar";
import { ServiceIcon } from "../service-icon";
import type { SearchServiceItem } from "@/store/search-bar/lib";
import { searchServiceItems } from "@/store/search-bar/lib";
import { handleOpenLink } from "@/lib/platform";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { cn } from "@/lib/utils";

export const SearchBar = ({
  isHidePopularServices = false,
}: {
  isHidePopularServices?: boolean;
}) => {
  const { isMobile } = useWindowDimensions();

  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [queryParams, setQueryParams] = useState<string>("");
  const [filteredServices, setFilteredServices] = useState<SearchServiceItem[]>(
    [],
  );

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number>(0);

  const [animationStates, setAnimationStates] = useState<{
    [key: string]: boolean;
  }>({});

  const { push } = useRoute();
  const { recentSearches, addRecentSearch, removeRecentSearch, setIsOnSearch } =
    useSearchStore();

  // Filter services based on local query
  useEffect(() => {
    const queryParam = queryParams.toLowerCase().trim();
    const filtered = queryParam
      ? searchServiceItems().filter((item) =>
          item.synonyms.some((synonym) =>
            synonym.toLowerCase().includes(queryParam),
          ),
        )
      : [];
    setFilteredServices(filtered);
  }, [queryParams]);

  const handleServiceClick = (service: SearchServiceItem) => {
    if (service.type === "landing-page") {
      handleOpenLink(service.url);
    } else {
      push(service.url);
    }
    addRecentSearch(service);
    handleClose();
  };

  const handleInputChange = useCallback((value: string) => {
    setQueryParams(value);
    setIsExpanded(true);
    setIsOnSearch(true);
  }, []);

  // To calculate the height of the content when expanded changes animation
  useEffect(() => {
    if (isExpanded && contentRef.current) {
      // Set initial height to 0 to enable animation
      setContentHeight(0);

      // Force a reflow before setting the actual height
      void contentRef.current.offsetHeight;

      // Set the actual height for animation
      const height = contentRef.current.scrollHeight;
      setContentHeight(height);

      // Set up staggered animations
      const animationTimings: { [key: string]: number } = {
        recentSection: 100,
        searchResultSection: 200,
        popularSection: 300,
      };

      // Create animation timers for staggered effect
      const timers: ReturnType<typeof setTimeout>[] = [];

      Object.entries(animationTimings).forEach(([key, delay]) => {
        const timer = setTimeout(() => {
          setAnimationStates((prev) => ({ ...prev, [key]: true }));
        }, delay);
        timers.push(timer);
      });

      // For service items interactions
      popularServiceItems().forEach((_, index) => {
        const timer = setTimeout(
          () => {
            setAnimationStates((prev) => ({
              ...prev,
              [`service_${index}`]: true,
            }));
          },
          350 + index * 50,
        );
        timers.push(timer);
      });

      return () => {
        timers.forEach((timer) => clearTimeout(timer));
      };
    } else {
      setContentHeight(0);
      setAnimationStates({});
    }
  }, [isExpanded, recentSearches.length, filteredServices.length, queryParams]);

  const handleClose = (): void => {
    setIsExpanded(false);
    setIsOnSearch(false);
    setQueryParams("");
    setFilteredServices([]);
  };

  // Handle clicking outside to close
  useEffect(() => {
    if (!isMobile) {
      const handleClickOutside = (event: MouseEvent): void => {
        if (
          containerRef.current &&
          event.target instanceof Node &&
          !containerRef.current.contains(event.target)
        ) {
          handleClose();
        }
      };

      if (isExpanded) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isExpanded, isMobile]);

  const handleRemove = (service: SearchServiceItem): void => {
    removeRecentSearch(service);
  };

  return (
    <>
      <View className="relative z-[100] flex-1">
        <div
          ref={containerRef}
          className={cn("rounded-t-3xl bg-white transition-all", {
            "rounded-t-3xl bg-white": isExpanded && !isHidePopularServices,
            "rounded-full bg-white": !isExpanded,
          })}
        >
          <SearchInput
            onClick={() => {
              setIsExpanded(true);
              setIsOnSearch(true);
            }}
            onChange={handleInputChange}
            editable={false}
            pointerEvents="none"
          />

          {isExpanded && !isMobile && (
            <div
              className={cn(
                "left-0 w-full overflow-hidden rounded-b-3xl border-b border-gray-100 bg-white transition-all duration-300 ease-in-out",
                {
                  "absolute shadow-sm": !isHidePopularServices,
                  "translate-y-0 opacity-100": contentHeight > 0,
                  "-translate-y-5 opacity-0": contentHeight <= 0,
                },
              )}
              style={{ height: `${contentHeight}px` }}
            >
              <div ref={contentRef} className="p-4">
                {recentSearches.length > 0 && !queryParams && (
                  <View
                    className={cn("transform transition-all duration-300", {
                      "translate-y-0 opacity-100":
                        animationStates.recentSection,
                      "translate-y-2 opacity-0": !animationStates.recentSection,
                    })}
                  >
                    <View className="mb-2 flex flex-row items-center justify-between">
                      <Typography variant="body-lg">Recent</Typography>
                    </View>
                    {recentSearches.map((item) => (
                      <View key={item.title} className="cursor-pointer">
                        <View className="item flex w-full cursor-pointer flex-row items-center rounded-lg border-b border-secondary-border py-2 hover:bg-background-intermediate">
                          <View
                            onClick={() => handleServiceClick(item)}
                            className="flex flex-row items-center gap-2"
                          >
                            <Clock />
                            <Typography variant="body-lg">
                              {item.title}{" "}
                            </Typography>
                          </View>
                          <IconButton
                            className="ml-auto bg-transparent hover:bg-transparent"
                            onClick={() => handleRemove(item)}
                            iconName="x"
                            size="sm"
                            variant="tertiary"
                          />
                        </View>
                      </View>
                    ))}
                  </View>
                )}

                {queryParams.length > 0 && filteredServices.length > 0 && (
                  <View
                    className={cn("transform transition-all duration-300", {
                      "translate-y-0 opacity-100":
                        animationStates.searchResultSection,
                      "translate-y-2 opacity-0":
                        !animationStates.searchResultSection,
                    })}
                  >
                    <Typography variant="body-lg" className="mb-2">
                      {filteredServices.length > 0
                        ? "Search Results"
                        : "No results found"}
                    </Typography>
                    {filteredServices.map((service) => (
                      <View
                        key={service.title}
                        onClick={() => handleServiceClick(service)}
                        className="item flex w-full cursor-pointer flex-row items-center rounded-lg border-b border-secondary-border py-2 hover:bg-background-intermediate"
                      >
                        <View className="flex flex-row items-center">
                          <View
                            key={service.title}
                            className="mr-2 flex h-6 w-6 items-center justify-center text-gray-500"
                          >
                            <Image src={service.icon} />
                          </View>
                          <Typography variant="body-lg">
                            {service.title}
                          </Typography>
                        </View>
                      </View>
                    ))}
                  </View>
                )}

                {queryParams.length > 0 && !filteredServices.length && (
                  <View className="flex flex-col items-center">
                    <Typography variant="body-md">
                      Can't find it? Try our popular services!
                    </Typography>
                  </View>
                )}

                <View
                  className={`mt-4 transform transition-all duration-300 ${
                    animationStates.popularSection
                      ? "translate-y-0 opacity-100"
                      : "translate-y-2 opacity-0"
                  }`}
                >
                  <View className="flex flex-col gap-4">
                    <ListHeading title="Popular Services" icon={Sparkle} />
                    <View className="native:flex native:flex-row grid grid-flow-col grid-cols-4 gap-4">
                      {popularServiceItems().map((service, index) => (
                        <View
                          key={service.name}
                          className={`transform transition-all duration-300 ${
                            animationStates[`service_${index}`]
                              ? "translate-y-0 opacity-100"
                              : "translate-y-2 opacity-0"
                          }`}
                        >
                          <ServiceIcon key={service.title} {...service} />
                        </View>
                      ))}
                    </View>
                  </View>
                </View>
              </div>
            </div>
          )}
        </div>
      </View>

      {isExpanded && isMobile && (
        <Dialog open={isExpanded}>
          <DialogContent className="flex h-full max-w-none flex-col overflow-scroll p-0">
            <View className="bg-white">
              <View className="mx-4 flex flex-row items-center gap-1 rounded-full bg-white pt-6">
                <MagnifyingGlass className="absolute left-0 size-6 text-foreground-intermediate" />
                <Input
                  placeholder="Search for Services"
                  className="flex-1 rounded-full border-none pl-10"
                  value={queryParams}
                  onChangeText={handleInputChange}
                />
                <IconButton
                  iconName="x"
                  className="absolute right-2"
                  onClick={handleClose}
                  variant="ghost"
                />
              </View>

              <View className="rounded-lg border-gray-200 p-4">
                {recentSearches.length > 0 && !queryParams && (
                  <View className="mb-4">
                    <Typography variant="body-lg" color="foreground-default">
                      Recent
                    </Typography>
                    {recentSearches.map((item) => (
                      <View
                        key={item.title}
                        className="mt-2 flex flex-row items-center border-b border-secondary-border py-2"
                      >
                        <Clock className="mr-2 text-gray-500" />
                        <Typography
                          variant="body-lg"
                          className="cursor-pointer"
                          onClick={() => handleServiceClick(item)}
                        >
                          {item.title}
                        </Typography>
                        <IconButton
                          className="ml-auto"
                          onClick={() => handleRemove(item)}
                          iconName="x"
                          size="sm"
                          variant="tertiary"
                        />
                      </View>
                    ))}
                  </View>
                )}

                {queryParams.length > 0 && filteredServices.length > 0 && (
                  <View className="flex flex-col gap-2">
                    <Typography variant="body-lg" color="foreground-default">
                      {filteredServices.length > 0
                        ? "Search Results"
                        : "No results found"}
                    </Typography>
                    {filteredServices.map((service) => (
                      <View
                        key={service.title}
                        onClick={() => handleServiceClick(service)}
                        className="border-b border-secondary-border py-2"
                      >
                        <View className="flex flex-row items-center gap-2">
                          <Image src={service.icon} />
                          <Typography variant="body-lg">
                            {service.title}
                          </Typography>
                        </View>
                      </View>
                    ))}
                  </View>
                )}

                {queryParams.length > 0 && !filteredServices.length && (
                  <View className="flex flex-col items-center pb-6">
                    <Typography variant="body-md">
                      Can't find it? Try our popular services!
                    </Typography>
                  </View>
                )}

                <View>
                  <ListHeading title="Popular Services" icon={Sparkle} />
                  <View className="mt-4 flex flex-row justify-between gap-4">
                    {popularServiceItems().map((service) => (
                      <ServiceIcon key={service.name} {...service} />
                    ))}
                  </View>
                </View>
              </View>
            </View>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
